import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addMessage, setStatus, sendMessage } from '../redux/chatSlice';
import { getDocumentContext, applyAIResponse, testOfficeConnection, enableTrackChanges, testCommands, getDocumentAsDocxBlob, getFullDocument } from '../utils/office';
import { colors, fontSizes, fontWeights } from '../theme/theme';

const ChatInput = () => {
  const dispatch = useDispatch();
  const [input, setInput] = useState('');
  const [trackChangesWarning, setTrackChangesWarning] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const status = useSelector(state => state.chat.status);
  const token = useSelector(state => state.user.token);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [input]);

  // In ChatInput.js
  const  handleSend = async () => {
    if (!input.trim() || status === 'loading') return;

    const messageText = input.trim();
    setInput('');
    setError(null);
    setTrackChangesWarning(false); // Reset warning

    // Add user message to UI immediately
    dispatch(addMessage({ role: 'kullanıcı', message: messageText, /* ...timestamp */ }));
    dispatch(setStatus('loading'));

    try {
      // --- STEP 1: Get context from Word ---
      const documentContext = await getDocumentContext();
      const wordDocument = await getFullDocument();

      let result;
      try {
        result = await dispatch(sendMessage({
          message: messageText,
          context: documentContext,
          token,
          wordDocument,
        })).unwrap();
      } catch (backendError) {
        console.warn("⚠️ Backend not available, using mock data:", backendError);
        // Use mock data when backend is not available
      }

      if (result.wordFunctions && Array.isArray(result.wordFunctions)) {
        await executeWordFunctions(result.wordFunctions);
      }

    } catch (err) {
      const errorMessage = err?.message || 'Mesaj gönderilemedi veya bir hata oluştu.';
      console.error("❌ Error in handleSend:", errorMessage, err);
      setError(errorMessage);
      setTimeout(() => setError(null), 5000);
    } finally {
      dispatch(setStatus('idle')); // Ensure status is reset
    }
  };

  // Function to execute Word operations based on backend response
  const executeWordFunctions = async (wordFunctions) => {
    try {
      await Word.run(async (context) => {
        for (const func of wordFunctions) {
          const { action, content, options = {} } = func;
          
            switch (action) {
              case 'INSERT_TEXT_AT_END':
                const body = context.document.body;
                body.insertParagraph(content, Word.InsertLocation.end);
                break;
                
              case 'REPLACE_SELECTED_TEXT':
              const replaceRange = context.document.getSelection();
              replaceRange.insertText(content, Word.InsertLocation.replace);
              break;
              
            case 'REPLACE_BY_LOCATOR':
              const targetLocator = options.targetLocator;
              
              if (targetLocator) {
                try {
                  
                  // Try to find the paragraph by its uniqueLocalId
                  const paragraph = context.document.getParagraphByUniqueLocalId(targetLocator);
                  paragraph.load('text');
                  await context.sync();
                  
                  // Replace the text in the paragraph
                  paragraph.insertText(content, Word.InsertLocation.replace);
                  await context.sync();
                  
                } catch (error) {
                  console.error(`❌ Error finding element by uniqueLocalId ${targetLocator}:`, error);
                  
                  // Fallback: try to find by searching through all paragraphs
                  try {
                    const paragraphs = context.document.body.paragraphs;
                    paragraphs.load('items');
                    await context.sync();
                    
                    let found = false;
                    for (let i = 0; i < paragraphs.items.length; i++) {
                      const paragraph = paragraphs.items[i];
                      paragraph.load('text, uniqueLocalId');
                      await context.sync();
                      
                      if (paragraph.uniqueLocalId === targetLocator) {
                        paragraph.insertText(content, Word.InsertLocation.replace);
                        await context.sync();
                        found = true;
                        break;
                      }
                    }
                    
                    if (!found) {
                      const body = context.document.body;
                      body.insertParagraph(content, Word.InsertLocation.end);
                      await context.sync();
                    }
                  } catch (fallbackError) {
                    console.error("❌ Fallback also failed:", fallbackError);
                    
                    // Final fallback: insert at end
                    try {
                      const body = context.document.body;
                      body.insertParagraph(content, Word.InsertLocation.end);
                      await context.sync();
                    } catch (finalError) {
                      console.error("❌ Final fallback also failed:", finalError);
                    }
                  }
                }
              }
              break;
              
            case 'DELETE_BY_LOCATOR':
              const deleteTargetLocator = options.targetLocator;
              
              if (deleteTargetLocator) {
                try {
                  
                  // Find the paragraph by its uniqueLocalId
                  const paragraphToDelete = context.document.getParagraphByUniqueLocalId(deleteTargetLocator);
                  paragraphToDelete.load('text');
                  await context.sync();
                  
                  // Delete the paragraph
                  paragraphToDelete.delete();
                  await context.sync();
                  
                } catch (error) {
                  console.error(`❌ Error deleting paragraph with uniqueLocalId ${deleteTargetLocator}:`, error);
                  
                  // Fallback: try to find by searching through all paragraphs
                  try {
                    const paragraphs = context.document.body.paragraphs;
                    paragraphs.load('items');
                    await context.sync();
                    
                    let found = false;
                    for (let i = 0; i < paragraphs.items.length; i++) {
                      const paragraph = paragraphs.items[i];
                      paragraph.load('text, uniqueLocalId');
                      await context.sync();
                      
                      if (paragraph.uniqueLocalId === deleteTargetLocator) {
                        paragraph.delete();
                        await context.sync();
                        found = true;
                        break;
                      }
                    }
                    
                    if (!found) {
                      console.log("⚠️ ~ executeWordFunctions ~ paragraph not found for deletion");
                    }
                  } catch (fallbackError) {
                    console.error("❌ Fallback also failed:", fallbackError);
                  }
                }
              }
              break;
              
            case 'INSERT_TEXT_AFTER_LOCATOR':
              const insertTargetLocator = options.targetLocator;
              
              if (insertTargetLocator) {
                try {                  
                  // Find the paragraph by its uniqueLocalId
                  const targetParagraph = context.document.getParagraphByUniqueLocalId(insertTargetLocator);
                  targetParagraph.load('text');
                  await context.sync();
                                    
                  // Insert new paragraph after the target paragraph
                  targetParagraph.insertParagraph(content, Word.InsertLocation.after);
                  await context.sync();
                  
                } catch (error) {
                  console.error(`❌ Error inserting after paragraph with uniqueLocalId ${insertTargetLocator}:`, error);
                  // Fallback: insert at the end
                  try {
                    const body = context.document.body;
                    body.insertParagraph(content, Word.InsertLocation.end);
                    await context.sync();
                  } catch (fallbackError) {
                    console.error("❌ Fallback also failed:", fallbackError);
                  }
                }
              }
              break;
              
            case 'ADD_BOLD_TEXT':
              const boldRange = context.document.getSelection();
              const newBoldRange = boldRange.insertText(content, Word.InsertLocation.replace);
              newBoldRange.font.bold = true;
              break;
              
            case 'ADD_ITALIC_TEXT':
              const italicRange = context.document.getSelection();
              const newItalicRange = italicRange.insertText(content, Word.InsertLocation.replace);
              newItalicRange.font.italic = true;
              break;
              
            case 'ADD_HEADING':
              const headingBody = context.document.body;
              const headingParagraph = headingBody.insertParagraph(content, Word.InsertLocation.end);
              headingParagraph.style = `Heading ${options.level || 1}`;
              break;
              
            case 'ADD_BULLET_LIST':
              const listBody = context.document.body;
              if (Array.isArray(content)) {
                for (let index = 0; index < content.length; index++) {
                  const item = content[index];
                  const listParagraph = listBody.insertParagraph(`• ${item}`, Word.InsertLocation.end);
                  // Apply bullet list formatting
                  listParagraph.style = 'List Paragraph';
                  await context.sync();
                }
              }
              break;
              
            case 'ADD_NUMBERED_LIST':
              const numberedListBody = context.document.body;
              if (Array.isArray(content)) {
                for (let index = 0; index < content.length; index++) {
                  const item = content[index];
                  const listParagraph = numberedListBody.insertParagraph(`${index + 1}. ${item}`, Word.InsertLocation.end);
                  // Apply numbered list formatting
                  listParagraph.style = 'List Number';
                  await context.sync();
                }
              }
              break;
              
            case 'SET_FONT_SIZE':
              const fontSizeRange = context.document.getSelection();
              fontSizeRange.font.size = options.size || 12;
              break;
              
            case 'SET_FONT_COLOR':
              const fontColorRange = context.document.getSelection();
              fontColorRange.font.color = options.color || 'black';
              break;
              
            case 'SET_PARAGRAPH_ALIGNMENT':
              const alignmentRange = context.document.getSelection();
              switch (options.alignment) {
                case 'center':
                  alignmentRange.paragraphFormat.alignment = Word.Alignment.center;
                  break;
                case 'right':
                  alignmentRange.paragraphFormat.alignment = Word.Alignment.right;
                  break;
                case 'justify':
                  alignmentRange.paragraphFormat.alignment = Word.Alignment.justified;
                  break;
                default:
                  alignmentRange.paragraphFormat.alignment = Word.Alignment.left;
              }
              break;
              
            case 'ADD_PAGE_BREAK':
              const pageBreakBody = context.document.body;
              pageBreakBody.insertBreak(Word.BreakType.page);
              break;
              
            case 'INSERT_TABLE':
              const tableBody = context.document.body;
              const rows = options.rows || 3;
              const columns = options.columns || 2;
              const table = tableBody.insertTable(rows, columns, Word.InsertLocation.end);
              if (Array.isArray(content)) {
                content.forEach((cellContent, index) => {
                  const row = Math.floor(index / columns);
                  const col = index % columns;
                  if (row < rows && col < columns) {
                    table.getCell(row, col).body.insertParagraph(cellContent, Word.InsertLocation.replace);
                  }
                });
              }
              break;
              
            case 'ENABLE_TRACK_CHANGES':
              context.document.trackChanges.enable();
              break;
              
            default:
              console.warn(`⚠️ Unknown Word action: ${action}`);
          }
        }
        
        await context.sync();
      });
      
    } catch (error) {
      console.error("❌ Error executing Word functions:", error);
      throw error;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    setIsTyping(e.target.value.length > 0);
  };

  const containerStyle = {
    padding: '12px 20px',
    background: colors.background,
    borderTop: `1px solid ${colors.border}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  };

  const inputRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const textareaStyle = {
    flex: 1,
    minHeight: '36px',
    maxHeight: '120px',
    padding: '8px 12px',
    borderRadius: '8px',
    border: 'none',
    background: colors.surface,
    color: colors.textPrimary,
    fontSize: fontSizes.body,
    fontFamily: 'inherit',
    resize: 'none',
    outline: 'none',
    transition: 'all 0.2s ease',
    lineHeight: '1.4',
    boxSizing: 'border-box',
  };

  const textareaFocusStyle = {
    background: colors.background,
    boxShadow: `0 0 0 2px ${colors.primary}`,
  };

  const sendIconStyle = {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: isTyping ? colors.primary : colors.border,
    color: isTyping ? colors.background : colors.textSecondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: isTyping ? 'pointer' : 'not-allowed',
    transition: 'all 0.2s ease',
    border: 'none',
    flexShrink: 0,
  };

  const sendIconHoverStyle = {
    background: isTyping ? '#004494' : colors.border,
    transform: isTyping ? 'scale(1.1)' : 'none',
  };

  const warningStyle = {
    padding: '8px 12px',
    background: `rgba(245, 124, 0, 0.1)`,
    border: `1px solid rgba(245, 124, 0, 0.2)`,
    borderRadius: '8px',
    color: colors.warning,
    fontSize: fontSizes.caption,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const errorStyle = {
    padding: '8px 12px',
    background: `rgba(211, 47, 47, 0.1)`,
    border: `1px solid rgba(211, 47, 47, 0.2)`,
    borderRadius: '8px',
    color: colors.error,
    fontSize: fontSizes.caption,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const characterCountStyle = {
    fontSize: fontSizes.small,
    color: colors.textSecondary,
    textAlign: 'right',
    marginTop: '4px',
  };

  const sendIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
    </svg>
  );

  return (
    <div style={containerStyle}>
      <div style={inputRowStyle}>
        <textarea
          ref={textareaRef}
          style={{
            ...textareaStyle,
            ...(isTyping ? textareaFocusStyle : {})
          }}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Mesajınızı yazın... (Enter ile gönder)"
          disabled={status === 'loading'}
          rows={1}
        />
        <button
          style={sendIconStyle}
          onClick={handleSend}
          disabled={!isTyping || status === 'loading'}
          onMouseEnter={(e) => {
            if (isTyping) {
              Object.assign(e.target.style, sendIconHoverStyle);
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.background = isTyping ? colors.primary : colors.border;
            e.target.style.transform = 'none';
          }}
        >
          {status === 'loading' ? (
            <div style={{
              width: '12px',
              height: '12px',
              border: '2px solid transparent',
              borderTop: '2px solid currentColor',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          ) : (
            sendIcon
          )}
        </button>
      </div>

      {/* Character count */}
      {isTyping && (
        <div style={characterCountStyle}>
          {input.length} karakter
        </div>
      )}

      {/* Warnings and errors */}
      {trackChangesWarning && (
        <div style={warningStyle}>
          ⚠️ Track Changes devre dışı; değişiklikler doğrudan uygulanacak.
        </div>
      )}

      {error && (
        <div style={errorStyle}>
          ❌ {error}
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        textarea::placeholder {
          color: ${colors.textSecondary};
          opacity: 0.7;
        }
      `}</style>
    </div>
  );
};

export default ChatInput; 